import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Field, fieldInputCls } from "@/components/Field";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";

export const GarageSelect = ({ label, items, value, onChange, testid }) => {
  const { t } = useT();
  const missing = value && !items.some((it) => it.id === value);
  return (
    <Field label={label}>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
        <SelectTrigger data-testid={testid} className={fieldInputCls}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t("none")}</SelectItem>
          {missing && <SelectItem value={value}>…</SelectItem>}
          {items.map((it) => (
            <SelectItem key={it.id} value={it.id} data-testid={`${testid}-option-${it.id}`}>
              {it.name}{it.serial ? ` · ${it.serial}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};

export const OptionSelect = ({ label, options, value, onChange, testid }) => {
  const { t } = useT();
  const opts = value && !options.includes(value) ? [...options, value] : options;
  return (
    <Field label={label}>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger data-testid={testid} className={fieldInputCls}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t("none")}</SelectItem>
          {opts.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-option-${o}`}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
};

const LapPair = ({ form, set, n }) => {
  const { t } = useT();
  const [lapKey, lapsKey, lapId, lapsId] = n === 1
    ? ["best_lap", "laps", "session-best-lap-input", "session-laps-input"]
    : ["best_lap2", "laps2", "session-best-lap2-input", "session-laps2-input"];
  const lapLabel = n === 2 ? t("best_lap_s2") : form.pit ? t("best_lap_s1") : t("best_lap");
  return (
    <div className="grid grid-cols-[1fr_110px] gap-3">
      <Field label={lapLabel}>
        <Input data-testid={lapId} placeholder={t("best_lap_hint")} value={form[lapKey] ?? ""} onChange={set(lapKey)} className={`${fieldInputCls} text-[#10B981] font-bold`} />
      </Field>
      <Field label={n === 2 || form.pit ? `${t("laps")} · S${n}` : t("laps")}>
        <Input data-testid={lapsId} type="number" min="0" inputMode="numeric" value={form[lapsKey] ?? ""} onChange={set(lapsKey)} className={fieldInputCls} />
      </Field>
    </div>
  );
};

export const GeneralFields = ({ form, setForm, garage, event }) => {
  const { t } = useT();
  const hideCat = useSettings().settings.hide_categories;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const empty = garage.engines.length === 0 && garage.chassis.length === 0;
  const noLists = event && !event.drivers?.length && !event.categories?.length;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <OptionSelect label={t("driver")} testid="session-driver-select" options={event?.drivers || []} value={form.driver} onChange={(v) => setForm({ ...form, driver: v })} />
      {!hideCat && <OptionSelect label={t("category")} testid="session-category-select" options={event?.categories || []} value={form.category} onChange={(v) => setForm({ ...form, category: v })} />}
      {hideCat && <div className="hidden sm:block" />}
      {noLists && <p data-testid="event-lists-hint" className="sm:col-span-2 font-mono text-xs text-[#FFB800]">{t("event_lists_hint")}</p>}
      <Field label={`${t("session_name")} *`}>
        <Input data-testid="session-name-input" required placeholder={t("session_name_hint")} value={form.name} onChange={set("name")} className={fieldInputCls} />
      </Field>
      <div className="grid grid-cols-[1fr_120px] gap-3">
        <Field label={t("session_date")}>
          <Input data-testid="session-date-input" type="date" value={form.session_date || ""} onChange={set("session_date")} className={`${fieldInputCls} [color-scheme:dark]`} />
        </Field>
        <Field label={t("session_time")}>
          <Input data-testid="session-time-input" type="time" value={form.session_time || ""} onChange={set("session_time")} className={`${fieldInputCls} [color-scheme:dark]`} />
        </Field>
      </div>
      <LapPair form={form} set={set} n={1} />
      <label className="flex items-center gap-3 bg-[#090A0F] border border-[#282D3D] rounded-sm px-3 h-10 self-end cursor-pointer">
        <Switch data-testid="session-pit-switch" checked={!!form.pit} onCheckedChange={(v) => setForm({ ...form, pit: v })} className="data-[state=checked]:bg-[#FFB800]" />
        <span className="font-heading uppercase font-bold text-lg text-[#FFB800]">{t("pit")}</span>
        <span className="font-mono text-[10px] text-slate-500 truncate">{t("pit_hint")}</span>
      </label>
      {form.pit && <><LapPair form={form} set={set} n={2} /><div className="hidden sm:block" /></>}
      <GarageSelect label={t("chassis")} testid="session-chassis-select" items={garage.chassis} value={form.chassis_id} onChange={(v) => setForm({ ...form, chassis_id: v })} />
      <GarageSelect label={t("engine")} testid="session-engine-select" items={garage.engines} value={form.engine_id} onChange={(v) => setForm({ ...form, engine_id: v })} />
      {empty && (
        <Link to="/garage" data-testid="garage-empty-hint" className="sm:col-span-2 font-mono text-xs text-[#FFB800] hover:underline">{t("garage_empty_hint")}</Link>
      )}
    </div>
  );
};
